import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { searchRecipients, type TrustedRecipient } from '@/api/recipients';
import { insertRecipient, recipientToken, normalizeRecipientInput } from '@/lib/recipient-input';

export function RecipientInput({ value, onChange, label = 'To' }: { value: string; onChange: (value: string) => void; label?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [caret, setCaret] = useState(value.length);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState(0);
  const [result, setResult] = useState<{ query: string; recipients: TrustedRecipient[] }>({ query: '', recipients: [] });
  const [failed, setFailed] = useState(false);
  const query = recipientToken(value, caret).query;
  const matches = result.query === query ? result.recipients : [];
  const open = focused && !dismissed && matches.length > 0;
  // Caret moves are applied right after React writes the new value, never on a later
  // animation frame: a late frame lands after the next keystroke and puts the caret back
  // behind it (" bo" became ", ob"), or pulls focus back after the user moved on.
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const at = pendingCaret.current;
    if (at === null || !input.current) return;
    pendingCaret.current = null;
    input.current.setSelectionRange(at, at);
  });

  useEffect(() => {
    if (!focused || !query || dismissed) return;
    let cancelled = false;
    setFailed(false);
    const timer = setTimeout(() => {
      searchRecipients(query).then(data => {
        if (!cancelled) { setResult({ query, recipients: data.recipients }); setActive(0); }
      }).catch(() => {
        if (!cancelled) { setResult({ query, recipients: [] }); setFailed(true); }
      });
    }, 180);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, focused, dismissed]);

  const choose = (recipient: TrustedRecipient) => {
    const next = insertRecipient(value, caret, recipient.email);
    onChange(next.value);
    setCaret(next.caret);
    setDismissed(true);
    setResult({ query: '', recipients: [] });
    pendingCaret.current = next.caret;
  };

  return (
    <div className="relative min-w-0 flex-1">
      <input ref={input} type="text" value={value} role="combobox" aria-label={label}
        aria-autocomplete="list" aria-expanded={open} aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        autoComplete="off" placeholder="Name or email address"
        className="w-full rounded-lg border border-bmail-border bg-bmail-navy px-3 py-2 text-sm text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
        onFocus={() => { setFocused(true); setDismissed(false); }}
        onBlur={() => { setFocused(false); setResult({ query: '', recipients: [] }); }}
        onSelect={e => setCaret(e.currentTarget.selectionStart ?? value.length)}
        onChange={e => {
          const raw = e.target.value;
          const next = normalizeRecipientInput(raw, e.target.selectionStart ?? raw.length);
          onChange(next.value); setCaret(next.caret); setDismissed(false); setActive(0);
          if (next.value !== raw) pendingCaret.current = next.caret;
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') { setDismissed(true); return; }
          if (!open) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(i => (i + (e.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length);
          } else if (e.key === 'Enter') { e.preventDefault(); choose(matches[active] || matches[0]); }
        }} />
      {open && <ul id={listId} role="listbox" aria-label="Trusted recipients"
        className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-lg border border-bmail-border bg-bmail-surface shadow-xl">
        {matches.map((recipient, index) => (
          <li key={recipient.email} id={`${listId}-${index}`} role="option" aria-selected={index === active}
            className={`cursor-pointer px-3 py-2 text-sm ${index === active ? 'bg-bmail-surface-hover' : ''}`}
            onMouseEnter={() => setActive(index)} onMouseDown={e => e.preventDefault()} onClick={() => choose(recipient)}>
            {recipient.name && <div className="truncate font-medium text-bmail-text">{recipient.name}</div>}
            <div className="truncate text-bmail-text-secondary">{recipient.email}</div>
          </li>
        ))}
      </ul>}
      {focused && failed && !dismissed && <div role="status" className="text-xs text-bmail-text-secondary">Suggestions unavailable. You can still type an address.</div>}
    </div>
  );
}
